import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clientsApi, healthApi } from '../lib/api';

interface Client {
  id: string;
  slug: string;
  companyName: string;
  city: string;
  niche: string;
  integrations: {
    wordpress: boolean;
    youtube: boolean;
    podbean: boolean;
    facebook: boolean;
    gbp: boolean;
  };
}

interface HealthStatus {
  status: string;
  checks: Record<string, { status: string }>;
}

export function DashboardPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [clientsRes, healthRes] = await Promise.all([
          clientsApi.list(),
          healthApi.check(),
        ]);
        setClients(clientsRes.clients);
        setHealth(healthRes);
      } catch (error) {
        console.error('Failed to load dashboard data', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Welcome to PAA Pipeline v2</p>
        </div>
        <Link
          to="/workflows/new"
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          New Workflow
        </Link>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatusBadge
            label="Database"
            status={health?.checks?.database?.status === 'ok'}
          />
          <StatusBadge
            label="Claude AI"
            status={health?.checks?.anthropic?.status === 'configured'}
          />
          <StatusBadge
            label="RenderForm"
            status={health?.checks?.renderform?.status === 'configured'}
          />
          <StatusBadge
            label="AutoContent"
            status={health?.checks?.autocontent?.status === 'configured'}
          />
          <StatusBadge
            label="Overall"
            status={health?.status === 'healthy'}
          />
        </div>
      </div>

      {/* Clients Overview */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Clients ({clients.length})
          </h2>
          <Link
            to="/clients/new"
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            + Add Client
          </Link>
        </div>

        {clients.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No clients yet.{' '}
            <Link to="/clients/new" className="text-primary-600 hover:underline">
              Add your first client
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {clients.map((client) => (
              <div
                key={client.id}
                className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div>
                  <Link
                    to={`/clients/${client.slug}`}
                    className="font-medium text-gray-900 hover:text-primary-600"
                  >
                    {client.companyName}
                  </Link>
                  <p className="text-sm text-gray-500">
                    {client.city} • {client.niche}
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <IntegrationDot
                    active={client.integrations.wordpress}
                    title="WordPress"
                  />
                  <IntegrationDot
                    active={client.integrations.youtube}
                    title="YouTube"
                  />
                  <IntegrationDot
                    active={client.integrations.podbean}
                    title="Podbean"
                  />
                  <IntegrationDot
                    active={client.integrations.facebook}
                    title="Facebook"
                  />
                  <IntegrationDot
                    active={client.integrations.gbp}
                    title="GBP"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickAction
          title="Run Workflow"
          description="Generate content for a PAA question"
          href="/workflows/new"
        />
        <QuickAction
          title="Manage Clients"
          description="Add or edit client profiles"
          href="/clients"
        />
        <QuickAction
          title="View History"
          description="See past workflow runs"
          href="/workflows"
        />
      </div>
    </div>
  );
}

function StatusBadge({ label, status }: { label: string; status: boolean }) {
  return (
    <div className="flex items-center space-x-2">
      <div
        className={`w-3 h-3 rounded-full ${
          status ? 'bg-green-500' : 'bg-red-500'
        }`}
      />
      <span className="text-sm text-gray-600">{label}</span>
    </div>
  );
}

function IntegrationDot({ active, title }: { active: boolean; title: string }) {
  return (
    <div
      title={`${title}: ${active ? 'Configured' : 'Not configured'}`}
      className={`w-2 h-2 rounded-full ${
        active ? 'bg-green-500' : 'bg-gray-300'
      }`}
    />
  );
}

function QuickAction({
  title,
  description,
  href,
}: {
  title: string;
  description: string;
  href: string;
}) {
  return (
    <Link
      to={href}
      className="block bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
    >
      <h3 className="font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-500 mt-1">{description}</p>
    </Link>
  );
}
